import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { CurrencySwitcher } from '@/components/layout/CurrencySwitcher';
import { usePlans, type PlanCatalogEntry } from '@/lib/billing';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import {
  currentDisplayCurrency,
  fromUsd,
  formatDisplayCurrency,
} from '@/lib/displayCurrency';

type PlanKey = 'free' | 'growth' | 'global' | 'enterprise';

interface Plan {
  key: PlanKey;
  name: string;
  monthlyUsdPerSeat: number;
  unit: string;
  features: string[];
  cta: string;
  highlight: boolean;
}

export function PricingPage() {
  const { t, i18n } = useTranslation();
  const [currency, setCurrency] = useState(currentDisplayCurrency());
  useDocumentMeta({
    title: 'Pricing — KeenEZ',
    description: 'Public per-employee pricing for KeenEZ. Free, Growth ($8), Global ($19), Enterprise ($39). AI agents included on every tier.',
    path: '/pricing',
  });

  // Stripe-sourced live plan catalogue. Single source of truth — a
  // Stripe Dashboard price edit propagates to this page within the
  // backend cache TTL (~10 min) with zero code changes.
  const plansQ = usePlans();
  const planFor = (code: PlanKey): PlanCatalogEntry | null =>
    plansQ.data?.find((p) => p.code === code) ?? null;
  const priceUsd = (code: PlanKey): number => (planFor(code)?.unitPriceMonthlyCents ?? 0) / 100;

  const plans: Plan[] = [
    {
      key: 'free',
      name: t('pricing.free'),
      monthlyUsdPerSeat: priceUsd('free'),
      unit: t('pricing.free_unit', { defaultValue: '/ month, forever' }),
      features: [
        t('pricing.free_seats'),
        t('pricing.free_sources'),
        t('pricing.free_history'),
        t('pricing.free_support', { defaultValue: 'Email + community Discord support' }),
        t('pricing.free_ai', { defaultValue: 'All AI agents included' }),
      ],
      cta: t('pricing.cta_start_free', { defaultValue: 'Start free' }),
      highlight: false,
    },
    {
      key: 'growth',
      name: t('pricing.growth'),
      monthlyUsdPerSeat: priceUsd('growth'),
      unit: t('pricing.per_employee_month', { defaultValue: '/ employee / month' }),
      features: [
        t('pricing.growth_in_free', { defaultValue: 'Everything in Free' }),
        t('pricing.growth_integrations', { defaultValue: 'Unlimited integrations' }),
        t('pricing.growth_history', { defaultValue: '3 years history' }),
        t('pricing.growth_esign', { defaultValue: 'E-sign + approval flow (hanko-aware)' }),
        t('pricing.growth_chat', { defaultValue: 'Slack / Teams / LINE WORKS / Chatwork' }),
        t('pricing.growth_support', { defaultValue: 'Email support, < 24h SLA' }),
      ],
      cta: t('pricing.cta_start_growth', { defaultValue: 'Start Growth' }),
      highlight: false,
    },
    {
      key: 'global',
      name: t('pricing.global'),
      monthlyUsdPerSeat: priceUsd('global'),
      unit: t('pricing.per_employee_month', { defaultValue: '/ employee / month' }),
      features: [
        t('pricing.global_in_growth', { defaultValue: 'Everything in Growth' }),
        t('pricing.global_payroll', { defaultValue: 'JP + US + UK + DE payroll' }),
        t('pricing.global_currency', { defaultValue: 'Multi-currency reporting' }),
        t('pricing.global_ai', { defaultValue: 'Cross-border compliance AI' }),
        t('pricing.global_audit', { defaultValue: 'Audit log (basic)' }),
      ],
      cta: t('pricing.cta_start_global', { defaultValue: 'Start Global' }),
      highlight: true,
    },
    {
      key: 'enterprise',
      name: t('pricing.enterprise'),
      monthlyUsdPerSeat: priceUsd('enterprise'),
      unit: t('pricing.per_employee_month', { defaultValue: '/ employee / month' }),
      features: [
        t('pricing.enterprise_in_global', { defaultValue: 'Everything in Global' }),
        t('pricing.enterprise_sso', { defaultValue: 'SSO / SAML / SCIM' }),
        t('pricing.enterprise_history', { defaultValue: '7 years history (J-SOX-aligned)' }),
        t('pricing.enterprise_audit', { defaultValue: 'Full audit log + SOC2 report' }),
        t('pricing.enterprise_sla', { defaultValue: '99.9% SLA, dedicated CSM' }),
      ],
      cta: t('pricing.cta_try_enterprise', { defaultValue: 'Try Enterprise' }),
      highlight: false,
    },
  ];

  return (
    <div className="min-h-screen">
      <SiteHeader />

      <main className="mx-auto max-w-6xl px-6 py-12 md:py-16">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-wide text-accent-600 font-medium mb-3">Pricing</p>
            <h1 className="display text-4xl md:text-5xl font-semibold tracking-tight text-ink-900 serif-jp">
              {t('pricing.title')}
            </h1>
            <p className="mt-3 text-ink-600">
              {t('pricing.subtitle', { defaultValue: 'Every other HR vendor says "contact sales." We publish four tiers. Pick yours.' })}
            </p>
          </div>
          <CurrencySwitcher onChange={setCurrency} />
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p) => (
            <PlanCard
              key={p.key}
              plan={p}
              displayCurrency={currency}
              locale={i18n.language}
              loading={plansQ.isLoading}
            />
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-ink-200 bg-white p-6 md:p-8">
          <h3 className="text-lg font-semibold text-ink-900">
            {t('pricing.ai_included_title', { defaultValue: 'AI agents are included' })}
          </h3>
          <p className="mt-2 text-sm text-ink-600 max-w-3xl">
            {t('pricing.ai_included_body', { defaultValue: 'Every plan includes the AI agents — compliance Q&A, Border Buddy, onboarding, policy drafting, offer letters. Cited answers with authoritative sources, no configuration required, no separate API key to manage.' })}
          </p>
          <div className="mt-5">
            <Link to="/compare" className="text-sm font-semibold text-accent-700 hover:text-accent-600">
              {t('pricing.compare_link', { defaultValue: 'See how KeenEZ compares to SmartHR, Rippling, Deel →' })}
            </Link>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function PlanCard({
  plan,
  displayCurrency,
  locale,
  loading,
}: {
  plan: Plan;
  displayCurrency: string;
  locale: string;
  loading: boolean;
}) {
  // Stripe charges in USD; this just shows the user the rough
  // equivalent in their local currency so they recognise the
  // magnitude. The card statement will be in USD.
  const converted = fromUsd(plan.monthlyUsdPerSeat, displayCurrency);
  const priceLabel = loading
    ? '—'
    : plan.monthlyUsdPerSeat === 0
      ? formatDisplayCurrency(0, displayCurrency, locale)
      : formatDisplayCurrency(converted.amount, displayCurrency, locale);

  return (
    <div
      className={[
        'rounded-2xl bg-white p-6 transition',
        plan.highlight ? 'border-2 border-accent-500 shadow-glow' : 'border border-ink-200 hover:shadow-soft',
      ].join(' ')}
    >
      {plan.highlight && (
        <p className="text-[10px] uppercase tracking-wide font-bold text-accent-600 mb-2">
          Cross-border favorite
        </p>
      )}
      <h3 className="text-lg font-semibold text-ink-900">{plan.name}</h3>
      <p className="mt-3 num text-3xl font-bold text-ink-900 tabular-nums">{priceLabel}</p>
      <p className="text-xs text-ink-500 mt-1">{plan.unit}</p>
      <ul className="mt-5 space-y-2 text-sm text-ink-700">
        {plan.features.map((f) => (
          <li key={f} className="flex gap-2">
            <span className="text-accent-500 font-bold">✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/signup"
        className={[
          'mt-6 inline-flex items-center justify-center w-full px-4 py-2.5 rounded-xl text-sm font-semibold transition',
          plan.highlight ? 'gradient-primary text-white shadow-glow hover:shadow-glow-pink' : 'border border-border hover:bg-muted',
        ].join(' ')}
      >
        {plan.cta}
      </Link>
    </div>
  );
}
